/**
 * DragGhost — icône fantôme rendue en portail qui suit le curseur pendant
 * un drag de tab.
 *
 * @remarks
 * Migré du legacy `MainLayout` : pendant qu'un tab est draggué, ce composant
 * rend une copie de l'icône au-dessus du curseur. Utilise `createPortal` vers
 * `document.body` pour échapper aux contraintes d'overflow des panneaux.
 *
 * **Style** : carré 36×36, fond `--color-accent-bg`, bordure `--color-accent-border`,
 * shadow-md, opacity 0.9 — exactement comme V1.
 */

import { createPortal } from 'react-dom';
import type { TabConfig } from '@/app/registry';
import { useUIEphemeralStore } from '@/app/stores/use-ui-ephemeral-store';
import { Icon } from '@/shared/ui';

interface Props {
  /** Tabs disponibles, peu importe leur panneau (left + right concaténés). */
  allTabs: readonly TabConfig[];
}

export function DragGhost({ allTabs }: Props) {
  const draggingTab = useUIEphemeralStore((s) => s.draggingTab);

  if (draggingTab === null) return null;

  const tab = allTabs.find((t) => t.id === draggingTab.tabId);
  if (!tab?.icon) return null;

  const TabIcon = tab.icon;

  return createPortal(
    <div
      aria-hidden
      style={{
        position: 'fixed',
        left: draggingTab.ghostX,
        top: draggingTab.ghostY,
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none',
        zIndex: 'var(--z-drag)',
      }}
      className="text-primary inline-flex size-9 items-center justify-center rounded-sm border border-(--color-accent-border) bg-(--color-accent-bg) opacity-90 shadow-md"
    >
      <Icon icon={TabIcon} size="md" strokeWidth={2} />
    </div>,
    document.body,
  );
}
